import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cn } from "@/utils/cn";
import { cva, type VariantProps } from "class-variance-authority";

const buttonVariants = cva(
  "ring-offset-background focus-visible:ring-ring inline-flex cursor-pointer items-center justify-center rounded-md font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary:
          "border border-brand-primary-700 bg-brand-primary-500 text-brand-secondary-500 hover:bg-brand-primary-300 disabled:border-brand-primary-200 disabled:bg-brand-primary-100 disabled:text-brand-primary-800",
        secondary:
          "border border-gray-400 bg-gray-200 text-gray-900 hover:bg-gray-400 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:outline-none hover:focus-visible:ring-offset-white disabled:text-gray-400",
        destructive:
          "border border-orange-600 bg-orange-500 text-white hover:border-orange-600 hover:bg-orange-600 focus-visible:ring-blue-600 disabled:border-orange-700 disabled:bg-orange-800 disabled:opacity-100",
        outline:
          "border border-gray-300 bg-transparent text-black hover:bg-gray-200/20 dark:text-white",
        ghost: "hover:bg-gray-200/20 hover:text-black/80",
        link: "text-gray-800 hover:font-bold focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:outline-none hover:focus-visible:ring-offset-white disabled:cursor-default disabled:text-gray-300 dark:text-white dark:focus-visible:ring-offset-gray-800 disabled:dark:text-gray-400",
      },
      size: {
        default: "h-10 px-4 py-2 md:text-base 4k:text-[40px]",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        "sm-icon": "h-7 w-7",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  leftIconClassName?: string;
  rightIconClassName?: string;
}

// Maps non-default sizes to a smaller icon dimension; everything else uses h-5 w-5.
const iconSizeBySize: Partial<
  Record<NonNullable<VariantProps<typeof buttonVariants>["size"]>, string>
> = {
  sm: "h-4 w-4",
  "sm-icon": "h-4 w-4",
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      loading = false,
      className,
      variant,
      size,
      asChild = false,
      type = "button",
      children,
      leftIcon,
      rightIcon,
      leftIconClassName,
      rightIconClassName,
      ...props
    },
    ref,
  ) => {
    const Component = asChild ? Slot : "button";
    const hasIcons = !asChild && (!!leftIcon || !!rightIcon);
    const iconSize = (size ? iconSizeBySize[size] : undefined) ?? "h-5 w-5";
    const { disabled: disabledProp, ...rest } = props;
    const finalDisabled = loading || !!disabledProp;

    const renderIcon = (icon: React.ReactNode, iconClassName?: string) => (
      <span
        aria-hidden
        className={cn(
          "shrink-0 [&>svg]:h-full [&>svg]:w-full",
          iconSize,
          iconClassName,
        )}
      >
        {icon}
      </span>
    );

    return (
      <Component
        {...rest}
        className={cn(
          buttonVariants({ variant, size, className }),
          hasIcons && "gap-2",
        )}
        ref={ref}
        type={type}
        disabled={finalDisabled}
      >
        {hasIcons && leftIcon && renderIcon(leftIcon, leftIconClassName)}
        <Slottable>{children}</Slottable>
        {hasIcons && rightIcon && renderIcon(rightIcon, rightIconClassName)}
      </Component>
    );
  },
);

Button.displayName = "Button";

export { Button };
export { ButtonLabel } from "./button-label";
